import type { RootState } from '@store';
import type { ApiError, Device, DevicesResponse } from 'common/services/api.types';
import * as api from '../../common/api';
import { createAppSlice } from '../../store/createAppSlice';

export type CardOrBoxForm = typeof emptyAddHardware.box;
const emptyAddHardware = {
  box: {
    isLoading: false,
    list: [] as ReturnType<typeof mapHardware>[],
    serialNumber: '',
    isValid: true,
    isBundled: false,
    notEligibleToMove: false,
    addHardwareInfo: {} as Device['addHardwareInfo'],
  },
  card: {
    isLoading: false,
    list: [] as ReturnType<typeof mapHardware>[],
    serialNumber: '',
    isValid: true,
    isBundled: false,
    notEligibleToMove: false,
    addHardwareInfo: {} as Device['addHardwareInfo'],
  },
  isLoading: false,
  isBundledHardware: false,
  isSuccessful: true,
};

const initialState = {
  addHardware: emptyAddHardware,
};

const mapHardware = ({ serialNumber, name }: Device) => ({ serialNumber, name });

const hardwareSlice = createAppSlice({
  name: 'hardwareProducts',
  initialState,
  reducers: (create) => ({
    setHardwareSerialNumber: create.reducer<{ serialNumber: string; isValid: boolean }>((state, action) => {
      state.addHardware.box = {
        ...state.addHardware.box,
        serialNumber: action.payload.serialNumber,
        isValid: action.payload.isValid,
        list: [],
        notEligibleToMove: false,
        isBundled: false,
      };
    }),
    setCardSerialNumber: create.reducer<{ serialNumber: string; isValid: boolean }>((state, action) => {
      state.addHardware.card = {
        ...state.addHardware.card,
        serialNumber: action.payload.serialNumber,
        isValid: action.payload.isValid,
        list: [],
        notEligibleToMove: false,
        isBundled: false,
      };
    }),
    // THUNKS
    addHardwareProducts: create.asyncThunk(
      // HACK: in order to break circular type inference we need to specify the return type here
      // so that we can use "RootState" for casting getState() inside the thunk
      (_, { getState }): ReturnType<typeof api.addHardwareProduct> => {
        const state = getState() as RootState;
        const addHardware = state.hardwareProducts.addHardware;
        return api.addHardwareProduct(
          addHardware.box.serialNumber,
          addHardware.card.serialNumber,
          addHardware.isBundledHardware,
        );
      },
      {
        pending: (state) => {
          state.addHardware.isLoading = true;
          state.addHardware.isSuccessful = false;
        },
        fulfilled: (state) => {
          state.addHardware = emptyAddHardware;
        },
        rejected: (state) => {
          state.addHardware.isLoading = false;
          state.addHardware.isSuccessful = false;
        },
      },
    ),
    getHardwareBySerial: create.asyncThunk<DevicesResponse, string, { rejectValue: ApiError }>(
      async (serialNumber: string, { rejectWithValue }) => {
        try {
          const res = await api.getDevices(serialNumber);
          return res;
        } catch (error) {
          // TODO: needs testing
          const err: ApiError = error as unknown as ApiError;
          return rejectWithValue(err);
        }
      },
      {
        pending: (state) => {
          state.addHardware.box = {
            ...state.addHardware.box,
            list: [],
            isLoading: true,
          };
        },
        fulfilled: (state, action) => {
          const device = action.payload[0];
          const isBundled = device?.isBundled ?? false;
          const firstBundledDevice = device?.bundledDevices[0];

          let cardDataList = state.addHardware.card.list;
          let cardSerial = state.addHardware.card.serialNumber;
          let cardAddHardwareInfo = state.addHardware.card.addHardwareInfo;
          let isCardSerialValid = state.addHardware.card.isValid;
          let cardIsBundled = state.addHardware.card.isBundled;

          if (device && isBundled && firstBundledDevice) {
            cardDataList = device.bundledDevices.map(mapHardware);
            cardSerial = firstBundledDevice.serialNumber;
            isCardSerialValid = true;
            cardIsBundled = firstBundledDevice.isBundled;
            cardAddHardwareInfo = firstBundledDevice.addHardwareInfo;
          }
          // Assign new state
          state.addHardware.box = {
            ...state.addHardware.box,
            list: action.payload.map(mapHardware),
            isLoading: false,
            notEligibleToMove: false,
            addHardwareInfo: device?.addHardwareInfo || {},
            isBundled,
          };
          state.addHardware.card = {
            ...state.addHardware.card,
            list: cardDataList,
            serialNumber: cardSerial,
            isValid: isCardSerialValid,
            addHardwareInfo: cardAddHardwareInfo,
            isBundled: cardIsBundled,
          };
          state.addHardware.isBundledHardware = isBundled;
        },
        rejected: (state, action) => {
          const errorCode = action.payload && 'response' in action.payload ? action.payload.response?.status : 0;
          state.addHardware.box = {
            ...state.addHardware.box,
            list: [],
            isLoading: false,
            addHardwareInfo: {},
            notEligibleToMove: errorCode === 409,
          };
        },
      },
    ),
    // TODO: merge with getHardwareBySerial somehow?
    getCardBySerial: create.asyncThunk<DevicesResponse, string, { rejectValue: ApiError }>(
      async (serialNumber: string, { rejectWithValue }) => {
        try {
          const res = await api.getDevices(serialNumber);
          return res;
        } catch (error) {
          // TODO: needs testing
          const err: ApiError = error as unknown as ApiError;
          return rejectWithValue(err);
        }
      },
      {
        pending: (state) => {
          state.addHardware.card = {
            ...state.addHardware.card,
            list: [],
            addHardwareInfo: {},
            isLoading: true,
            notEligibleToMove: false,
          };
        },
        fulfilled: (state, action) => {
          const device = action.payload[0];
          const isBundled = device?.isBundled ?? false;
          const firstBundledDevice = device?.bundledDevices[0];

          let hardwareDataList = state.addHardware.box.list;
          let boxAddHardwareInfo = state.addHardware.box.addHardwareInfo;
          let hardwareSerial = state.addHardware.box.serialNumber;
          let isHardwareSerialValid = state.addHardware.box.isValid;
          let hardwareIsBundled = state.addHardware.box.isBundled;

          if (device && isBundled && firstBundledDevice) {
            hardwareDataList = device.bundledDevices.map(mapHardware);
            hardwareSerial = firstBundledDevice.serialNumber;
            isHardwareSerialValid = true;
            hardwareIsBundled = firstBundledDevice.isBundled;
            boxAddHardwareInfo = firstBundledDevice.addHardwareInfo;
          }
          // Assign new state
          state.addHardware.box = {
            ...state.addHardware.box,
            list: hardwareDataList,
            isValid: isHardwareSerialValid,
            serialNumber: hardwareSerial,
            addHardwareInfo: boxAddHardwareInfo,
            isBundled: hardwareIsBundled,
          };
          state.addHardware.card = {
            ...state.addHardware.card,
            list: action.payload.map(mapHardware),
            isLoading: false,
            notEligibleToMove: false,
            addHardwareInfo: device?.addHardwareInfo ?? {},
            isBundled,
          };
          state.addHardware.isBundledHardware = isBundled;
        },
        rejected: (state, action) => {
          const errorCode = action.payload && 'response' in action.payload ? action.payload.response?.status : 0;

          state.addHardware.card = {
            ...state.addHardware.card,
            list: [],
            isLoading: false,
            addHardwareInfo: {},
            notEligibleToMove: errorCode === 409,
          };
        },
      },
    ),
  }),
  selectors: {
    // Add hardware
    addBoxForm: (state) => state.addHardware.box,
    addCardForm: (state) => state.addHardware.card,
    addHardwareFormIsLoading: (state) => state.addHardware.isLoading,
    addHardwareRequestSuccessful: (state) => state.addHardware.isSuccessful,
    isMatchingHardwareEquipment: (state) => state.addHardware.box.isBundled === state.addHardware.card.isBundled,
    /**
     * Add Hardware Form, Step 1
     */
    isAddHardwareFormValid: (state) => {
      const data = state.addHardware;
      const formLoading = data.isLoading;
      const isHardwareValid = data.box.isValid;
      const isCardValid = data.card.isValid;
      const isMatchingHardware = data.box.isBundled === data.card.isBundled;

      if (formLoading || data.card.isLoading || data.box.isLoading) {
        return false;
      }
      if (data.card.addHardwareInfo.isBlocked || data.box.addHardwareInfo.isBlocked) {
        return false;
      }
      if (!data.card.serialNumber && !data.box.serialNumber) {
        return false;
      }
      if (!isMatchingHardware) return false;
      if (data.isBundledHardware) {
        return data.box.list.length > 0 && data.card.list.length > 0 && isHardwareValid;
      }
      return isHardwareValid && isCardValid && !data.card.notEligibleToMove;
    },
    /**
     * Add Hardware Form, Step 2
     */
    isAddHardwareFormStep2Valid: (state) => {
      const data = state.addHardware;
      const isHardwareValid = state.addHardware.box.isValid;
      const isCardValid = state.addHardware.card.isValid;
      if (data.card.addHardwareInfo.isBlocked || data.box.addHardwareInfo.isBlocked) {
        return false;
      }
      const listIsNotEmpty = data.card.list.length > 0 || data.box.list.length > 0;
      return isHardwareValid && isCardValid && listIsNotEmpty;
    },
  },
});

// Selectors that act on other slices need global root state as input

export const actions = hardwareSlice.actions;

export const selectors = hardwareSlice.selectors;

export default hardwareSlice.reducer;
